import axios, { AxiosError, type AxiosInstance } from "axios";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import type { ApiError } from "@/types/api";

/**
 * The one HTTP client.
 *
 * Same-origin only: Caddy serves the SPA from the tenant host, so django-tenants
 * resolves the tenant from the `Host` header and the browser never sends a
 * tenant hint. Never hardcode a hostname here (contract §7.4).
 */
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
  withCredentials: true, // session cookie: __Host-sessionid
  // `__Host-csrftoken`, matching CSRF_COOKIE_NAME in config/settings/base.py.
  // The prefix is the security property: a sibling subdomain (a dangling CNAME,
  // a taken-over marketing host) can set `csrftoken=...; Domain=.<base domain>`
  // but cannot set a `__Host-` name at all, so it cannot supply the other half
  // of the double-submit pair. Rename these two together or every write 403s.
  xsrfCookieName: "__Host-csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  headers: { Accept: "application/json" },
});

// ---------------------------------------------------------------------------
// Cross-cutting handlers, registered once by App.tsx.
//
// The interceptor runs outside React, so the app hands it callbacks rather than
// reaching for a router or a store singleton.
// ---------------------------------------------------------------------------

interface ApiHandlers {
  /** Called on 401 for a request that is not itself an auth bootstrap call. */
  onUnauthenticated: (() => void) | null;
  /** Called on 402 with the tenant's suspension reason. */
  onTenantSuspended: ((reason: string) => void) | null;
  /** Called on 403 and 5xx so the shell can raise a toast. */
  onNotify: ((error: ApiError) => void) | null;
}

const handlers: ApiHandlers = {
  onUnauthenticated: null,
  onTenantSuspended: null,
  onNotify: null,
};

export function registerApiHandlers(next: Partial<ApiHandlers>): void {
  Object.assign(handlers, next);
}

/** Requests whose 401 is a normal answer, not a session expiry. */
const AUTH_PROBE_PATHS = ["auth/session/", "auth/login/", "auth/csrf/", "auth/logout/"];

function isAuthProbe(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_PROBE_PATHS.some((p) => url.includes(p));
}

/**
 * Normalise anything axios can throw into `ApiError`.
 *
 * Every feature uses this; nobody reads `err.response.data` directly.
 */
export function toApiError(err: unknown): ApiError {
  if (isApiError(err)) return err;

  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<Record<string, unknown>>;
    const status = axiosErr.response?.status ?? 0;
    const data = axiosErr.response?.data;

    if (data && typeof data === "object") {
      const detail = typeof data.detail === "string" ? data.detail : axiosErr.message;
      const code = typeof data.code === "string" ? data.code : fallbackCode(status);
      const errors =
        data.errors && typeof data.errors === "object"
          ? (data.errors as Record<string, string[]>)
          : null;
      // Spread first so the four canonical fields always win, and any extra key the
      // endpoint sent (`member_id` and `candidates` on the conversion 409,
      // `choices` on the ambiguous-login 409) survives normalisation.
      return { ...data, status, code, detail, errors };
    }

    return {
      status,
      code: fallbackCode(status),
      detail: status === 0 ? "Network unavailable" : axiosErr.message,
      errors: null,
    };
  }

  if (err instanceof Error) {
    return { status: 0, code: "unexpected_error", detail: err.message, errors: null };
  }

  return { status: 0, code: "unexpected_error", detail: String(err), errors: null };
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "status" in value &&
    "code" in value &&
    "detail" in value &&
    "errors" in value
  );
}

function fallbackCode(status: number): string {
  switch (status) {
    case 0:
      return "network_error";
    case 400:
      return "invalid";
    case 401:
      return "not_authenticated";
    case 402:
      return "tenant_suspended";
    case 403:
      return "permission_denied";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 429:
      return "throttled";
    default:
      return status >= 500 ? "server_error" : "error";
  }
}

api.interceptors.response.use(
  (response) => response,
  (rawError: unknown) => {
    const error = toApiError(rawError);
    const requestUrl = axios.isAxiosError(rawError) ? rawError.config?.url : undefined;

    if (error.status === 401 && !isAuthProbe(requestUrl)) {
      handlers.onUnauthenticated?.();
    } else if (error.status === 402) {
      const reason =
        axios.isAxiosError(rawError) &&
        typeof rawError.response?.data === "object" &&
        rawError.response?.data !== null &&
        "suspension_reason" in rawError.response.data
          ? String((rawError.response.data as Record<string, unknown>).suspension_reason ?? "")
          : "";
      handlers.onTenantSuspended?.(reason);
    } else if (error.status === 403 || error.status >= 500) {
      handlers.onNotify?.(error);
    }

    return Promise.reject(error);
  },
);

/**
 * Prime the `csrftoken` cookie. Called once at startup, before any mutation:
 * axios reads the cookie itself and sets `X-CSRFToken` from it.
 */
export async function fetchCsrfToken(): Promise<void> {
  try {
    await api.get("auth/csrf/");
  } catch {
    // A missing CSRF cookie surfaces as a 403 on the first mutation, which the
    // interceptor already reports. Never block app start on it.
  }
}

/**
 * Map a 400's field errors onto a react-hook-form instance.
 *
 * Returns true when at least one error landed on a known field, so callers know
 * whether they still owe the user a toast.
 */
export function applyServerErrors<TValues extends FieldValues>(
  form: UseFormReturn<TValues>,
  rawError: unknown,
  fieldMap: Partial<Record<string, Path<TValues>>> = {},
): boolean {
  const error = toApiError(rawError);
  if (!error.errors) return false;

  let applied = false;
  const known = new Set(Object.keys(form.getValues() ?? {}));

  for (const [rawField, messages] of Object.entries(error.errors)) {
    const message = Array.isArray(messages) ? messages.join(" ") : String(messages);
    const mapped = fieldMap[rawField] ?? (rawField as Path<TValues>);

    if (rawField === "non_field_errors" || (!known.has(String(mapped)) && !fieldMap[rawField])) {
      form.setError("root", { type: "server", message });
      continue;
    }

    form.setError(mapped, { type: "server", message });
    applied = true;
  }

  return applied;
}

/** Strip undefined/empty params so query keys and URLs stay stable. */
export function cleanParams<T extends Record<string, unknown>>(params: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
}
